import { useCallback, useEffect, useState } from "react";
import { Lock, Unlock, BookOpen, RotateCcw } from "lucide-react";
import { staffApi, apiError } from "../lib/api";

const SCOPES = [
  { key: "institution", label: "Whole choir" },
  { key: "group", label: "A group" },
  { key: "chorister", label: "One chorister" },
];

const BULK = [
  { action: "c1", label: "Challenge 1" },
  { action: "c2", label: "Challenge 2" },
  { action: "c3", label: "Challenge 3" },
  { action: "all_challenges", label: "All Challenges" },
  { action: "tests", label: "Topic Tests" },
];

export default function CurriculumPage() {
  const [modules, setModules] = useState([]);
  const [moduleId, setModuleId] = useState("");
  const [data, setData] = useState(null);
  const [scope, setScope] = useState("institution");
  const [groups, setGroups] = useState([]);
  const [choristers, setChoristers] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [choristerId, setChoristerId] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState("");
  const [bulkBusy, setBulkBusy] = useState("");

  const modKey = modules.find((m) => m.id === moduleId)?.key;
  const levelLabel = (t) => (modKey === "theory" ? `Grade ${t.grade}` : `Level ${t.grade}`);

  useEffect(() => {
    staffApi.get("/staff/modules").then((r) => {
      const enabled = r.data.modules.filter((m) => m.enabled);
      setModules(enabled);
      if (enabled.length) setModuleId((cur) => cur || enabled[0].id);
    }).catch((e) => setError(apiError(e)));
    staffApi.get("/staff/groups").then((r) => setGroups(r.data.groups)).catch(() => {});
    staffApi.get("/staff/choristers").then((r) => setChoristers(r.data.choristers)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async (mid) => {
    if (!mid) return;
    try {
      const { data: d } = await staffApi.get(`/staff/unlocks?module_id=${mid}`);
      setData(d);
    } catch (e) {
      setError(apiError(e));
    }
  }, []);

  useEffect(() => { load(moduleId); }, [moduleId, load]);

  const matchesScope = (s) => {
    if (s.scope !== scope) return false;
    if (scope === "group") return s.group_id === groupId;
    if (scope === "chorister") return s.chorister_id === choristerId;
    return true;
  };

  const rowFor = (topicId, item) =>
    data?.states.find((s) => s.topic_id === topicId && s.item === item && matchesScope(s));

  const scopeReady = scope === "institution" || (scope === "group" && groupId) || (scope === "chorister" && choristerId);
  const scopeLabel = scope === "institution" ? "the whole choir"
    : scope === "group" ? `the group "${groups.find((g) => g.id === groupId)?.name || "…"}"`
    : `the chorister "${choristers.find((c) => c.id === choristerId)?.display_name || "…"}"`;

  const scopePayload = () => ({
    scope,
    group_id: scope === "group" ? groupId : null,
    chorister_id: scope === "chorister" ? choristerId : null,
  });

  const patchLocal = (topicId, item, unlockedOrNull) => {
    setData((d) => {
      const states = d.states.filter((s) => !(s.topic_id === topicId && s.item === item && matchesScope(s)));
      if (unlockedOrNull !== null) {
        states.push({ topic_id: topicId, item, unlocked: unlockedOrNull, ...scopePayload() });
      }
      return { ...d, states };
    });
  };

  const triState = scope !== "institution"; // institution is the base setting: simple on/off

  // Cycles Inherit -> Unlocked -> Locked -> Inherit for group/chorister; toggles on/off for institution.
  const cycle = async (topicId, item) => {
    if (!scopeReady) return;
    const key = `${topicId}:${item}`;
    setSaving(key);
    setError("");
    const cur = rowFor(topicId, item);
    try {
      if (!triState) {
        const next = !(cur?.unlocked);
        await staffApi.put("/staff/unlocks", { topic_id: topicId, item, unlocked: next, ...scopePayload() });
        patchLocal(topicId, item, next);
      } else if (!cur) {
        await staffApi.put("/staff/unlocks", { topic_id: topicId, item, unlocked: true, ...scopePayload() });
        patchLocal(topicId, item, true);
      } else if (cur.unlocked) {
        await staffApi.put("/staff/unlocks", { topic_id: topicId, item, unlocked: false, ...scopePayload() });
        patchLocal(topicId, item, false);
      } else {
        await staffApi.delete("/staff/unlocks", { data: { topic_id: topicId, item, ...scopePayload() } });
        patchLocal(topicId, item, null);
      }
    } catch (e) {
      setError(apiError(e));
    } finally {
      setSaving("");
    }
  };

  const clearTopic = async (t) => {
    const rows = (data?.states || []).filter((s) => s.topic_id === t.id && matchesScope(s));
    if (!rows.length) return;
    setSaving(`${t.id}:clear`);
    setError("");
    try {
      for (const r of rows) {
        await staffApi.delete("/staff/unlocks", { data: { topic_id: t.id, item: r.item, ...scopePayload() } });
      }
      setData((d) => ({ ...d, states: d.states.filter((s) => !(s.topic_id === t.id && matchesScope(s))) }));
    } catch (e) {
      setError(apiError(e));
    } finally {
      setSaving("");
    }
  };

  const bulk = async (action, unlocked) => {
    if (!scopeReady || bulkBusy) return;
    const label = BULK.find((b) => b.action === action)?.label || action;
    const warn = action === "tests" && unlocked
      ? `WARNING: this unlocks every Topic Test in the module for ${scopeLabel}. Tests are meant as delayed retrieval — only do this deliberately.\n\nProceed?`
      : `${unlocked ? "Unlock" : "Lock"} ${label} for ALL topics in this module for ${scopeLabel}?\n\nIndividual topics can still be overridden afterwards.`;
    if (!window.confirm(warn)) return;
    setBulkBusy(`${action}:${unlocked}`);
    setError("");
    try {
      await staffApi.post("/staff/unlocks/bulk", { module_id: moduleId, action, unlocked, ...scopePayload() });
      await load(moduleId);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBulkBusy("");
    }
  };

  const cellState = (topicId, item) => {
    const r = rowFor(topicId, item);
    if (!r) return triState ? "inherit" : "locked";
    return r.unlocked ? "unlocked" : "locked";
  };

  const CELL_CLS = {
    unlocked: "bg-[#2D6A4F] text-white hover:bg-[#245741]",
    locked: triState ? "bg-[#7A1C2E] text-white hover:bg-[#8F2438]" : "bg-[#F3EFE0] text-[#566270] hover:bg-[#E5DEC9]",
    inherit: "bg-white border-2 border-dashed border-[#C69B3D]/60 text-[#8a6a10] hover:bg-[#F6EED8]",
  };

  return (
    <div data-testid="curriculum-page">
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0B192C]">Curriculum Unlocks</h1>
      <p className="text-sm text-[#566270] mt-1 max-w-2xl">
        Lessons ("Learn") are always open. Challenges and Topic Tests stay locked until you unlock them here —
        so you can space learning over weeks and use the test as genuine delayed retrieval.
        For groups and individual choristers each cell is three-state: <strong>Inherit</strong> (follow the wider setting)
        → <strong>Unlocked</strong> → <strong>Locked</strong>. Click to cycle; "Clear overrides" returns a whole topic to Inherit.
        An individual override always wins; otherwise any unlocked group grants access; otherwise the whole-choir setting applies.
      </p>

      <div className="mt-5 flex flex-wrap gap-3 items-center">
        <select value={moduleId} onChange={(e) => setModuleId(e.target.value)} data-testid="curriculum-module-select"
          className="px-4 py-3 rounded-xl border-2 border-[#E5DEC9] bg-white text-sm font-semibold text-[#0B192C] min-h-[48px]">
          {modules.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
        <div className="flex rounded-xl border-2 border-[#E5DEC9] bg-white overflow-hidden" role="tablist" aria-label="Unlock scope">
          {SCOPES.map((s) => (
            <button key={s.key} onClick={() => setScope(s.key)} role="tab" aria-selected={scope === s.key}
              data-testid={`scope-tab-${s.key}`}
              className={`px-4 py-3 text-sm font-bold min-h-[48px] transition-colors ${scope === s.key ? "bg-[#0B192C] text-[#FAF7EE]" : "text-[#566270] hover:bg-[#F3EFE0]"}`}>
              {s.label}
            </button>
          ))}
        </div>
        {scope === "group" && (
          <select value={groupId} onChange={(e) => setGroupId(e.target.value)} data-testid="curriculum-group-select"
            className="px-4 py-3 rounded-xl border-2 border-[#E5DEC9] bg-white text-sm font-semibold text-[#0B192C] min-h-[48px]">
            <option value="">Choose a group…</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        )}
        {scope === "chorister" && (
          <select value={choristerId} onChange={(e) => setChoristerId(e.target.value)} data-testid="curriculum-chorister-select"
            className="px-4 py-3 rounded-xl border-2 border-[#E5DEC9] bg-white text-sm font-semibold text-[#0B192C] min-h-[48px]">
            <option value="">Choose a chorister…</option>
            {choristers.map((c) => <option key={c.id} value={c.id}>{c.display_name} ({c.username})</option>)}
          </select>
        )}
      </div>

      {scopeReady && data && (
        <div className="mt-4 rounded-2xl bg-white border border-[#E5DEC9] p-4" data-testid="bulk-bar">
          <p className="text-xs font-bold uppercase tracking-widest text-[#566270]">
            Bulk actions · {scopeLabel} · every topic in this module
          </p>
          <div className="mt-2 flex gap-2 flex-wrap">
            {BULK.map((b) => (
              <div key={b.action} className="flex rounded-xl overflow-hidden border border-[#E5DEC9]">
                <span className="px-3 py-2.5 text-xs font-bold text-[#0B192C] bg-[#F3EFE0] flex items-center">{b.label}</span>
                <button onClick={() => bulk(b.action, true)} disabled={!!bulkBusy}
                  data-testid={`bulk-unlock-${b.action}`}
                  className="px-3 py-2.5 text-xs font-bold text-[#2D6A4F] bg-white hover:bg-[#EBF6F0] min-h-[44px] disabled:opacity-50">
                  Unlock
                </button>
                <button onClick={() => bulk(b.action, false)} disabled={!!bulkBusy}
                  data-testid={`bulk-lock-${b.action}`}
                  className="px-3 py-2.5 text-xs font-bold text-[#7A1C2E] bg-white hover:bg-[#FBF0F2] min-h-[44px] disabled:opacity-50">
                  Lock
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && <p role="alert" className="mt-4 text-sm text-[#A62B2B]" data-testid="curriculum-error">{error}</p>}

      {!scopeReady ? (
        <p className="mt-8 text-sm text-[#566270]" data-testid="curriculum-pick-target">
          Choose {scope === "group" ? "a group" : "a chorister"} above to manage their unlocks.
        </p>
      ) : !data ? (
        <p className="mt-8 text-sm text-[#566270]">Loading…</p>
      ) : (
        <div className="mt-6 space-y-3">
          {data.topics.map((t) => {
            const hasOverrides = (data.states || []).some((s) => s.topic_id === t.id && matchesScope(s));
            return (
              <div key={t.id} className="rounded-2xl bg-white border border-[#E5DEC9] p-4 sm:p-5" data-testid={`curriculum-topic-${t.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display font-bold text-[#0B192C]">{t.name}</h3>
                  {t.grade && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#F3EFE0] text-[#566270]">{levelLabel(t)}</span>}
                  {triState && hasOverrides && (
                    <button onClick={() => clearTopic(t)} disabled={saving === `${t.id}:clear`}
                      data-testid={`clear-overrides-${t.id}`}
                      className="inline-flex items-center gap-1 text-[11px] font-bold text-[#7A1C2E] underline min-h-[32px] disabled:opacity-50">
                      <RotateCcw className="w-3 h-3" aria-hidden /> Clear overrides
                    </button>
                  )}
                </div>
                <div className="mt-3 flex gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1 px-3 py-2.5 rounded-xl bg-[#EBF6F0] text-[#2D6A4F] text-xs font-bold min-h-[44px]"
                    data-testid={`learn-open-${t.id}`}>
                    <BookOpen className="w-3.5 h-3.5" aria-hidden /> Learn · always open
                  </span>
                  {[...Array.from({ length: t.challenge_count }, (_, i) => `c${i + 1}`), "test"].map((item) => {
                    const st = cellState(t.id, item);
                    const label = item === "test" ? "Topic Test" : `Challenge ${item.slice(1)}`;
                    const suffix = triState ? (st === "inherit" ? " · Inherit" : st === "unlocked" ? " · Unlocked" : " · Locked") : "";
                    return (
                      <button key={item} onClick={() => cycle(t.id, item)} disabled={saving === `${t.id}:${item}`}
                        data-testid={`unlock-toggle-${t.id}-${item}`}
                        data-state={st}
                        aria-pressed={st === "unlocked"}
                        title={triState ? `${label}: ${st}. Click to cycle Inherit → Unlocked → Locked.` : `${label}: ${st}. Click to ${st === "unlocked" ? "lock" : "unlock"}.`}
                        className={`inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold min-h-[44px] transition-colors disabled:opacity-50 ${CELL_CLS[st]}`}>
                        {st === "unlocked" ? <Unlock className="w-3.5 h-3.5" aria-hidden /> : <Lock className="w-3.5 h-3.5" aria-hidden />}
                        {label}{suffix}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
